import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gavel, Star, Mic, ShoppingBag, Video, Ticket, Sparkles,
  Coins, Filter, Clock, Trophy, ArrowLeft, ChevronRight,
  TrendingUp, Users, Flame, AlertTriangle,
} from "lucide-react";

import { toast } from "sonner";
import { popCredits } from "@/components/CreditPop";
import { fireConfetti, haptic } from "@/lib/celebrate";
import { ArtistCreateFab } from "@/components/ArtistCreateFab";

const TYPE_ICONS = {
  shoutout: Mic,
  exclusive: Star,
  merch: ShoppingBag,
  meet: Video,
  ticket: Ticket,
  custom: Sparkles,
};

const TYPE_COLORS = {
  shoutout: "from-pink-500 to-rose-400",
  exclusive: "from-[#7000FF] to-purple-400",
  merch: "from-[#FF6B35] to-orange-400",
  meet: "from-blue-500 to-cyan-400",
  ticket: "from-emerald-500 to-green-400",
  custom: "from-gray-500 to-gray-400",
};

function useCountdown(endDate) {
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0, expired: false });

  useEffect(() => {
    if (!endDate) return;
    const calc = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      return { d, h, m, s, expired: false };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  return timeLeft;
}

function CountdownDisplay({ endDate, compact = false }) {
  const { d, h, m, s, expired } = useCountdown(endDate);

  if (expired) {
    return (
      <span className="text-red-500 font-bold text-xs">Terminee</span>
    );
  }

  if (compact) {
    const isUrgent = d === 0 && h === 0 && m < 5;
    if (d > 0) return <span className="font-mono-credits text-xs text-[#FF6B35]">{d}j {h}h {String(m).padStart(2,"0")}m {String(s).padStart(2,"0")}s</span>;
    if (h > 0) return <span className="font-mono-credits text-xs text-[#FF6B35]">{h}h {String(m).padStart(2,"0")}m {String(s).padStart(2,"0")}s</span>;
    return <span className={`font-mono-credits text-xs ${isUrgent ? "text-red-500 animate-pulse font-bold" : "text-[#FF6B35]"}`}>{m}:{String(s).padStart(2, "0")}</span>;
  }

  return (
    <div className="flex gap-1.5">
      {d > 0 && (
        <div className="bg-popover rounded-lg px-2 py-1 text-center min-w-[36px]">
          <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{d}</p>
          <p className="text-[8px] text-muted-foreground">JOURS</p>
        </div>
      )}
      <div className="bg-popover rounded-lg px-2 py-1 text-center min-w-[36px]">
        <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{String(h).padStart(2, "0")}</p>
        <p className="text-[8px] text-muted-foreground">H</p>
      </div>
      <div className="bg-popover rounded-lg px-2 py-1 text-center min-w-[36px]">
        <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{String(m).padStart(2, "0")}</p>
        <p className="text-[8px] text-muted-foreground">MIN</p>
      </div>
      <div className="bg-popover rounded-lg px-2 py-1 text-center min-w-[36px]">
        <p className={`font-mono-credits font-bold text-sm ${h === 0 && d === 0 ? "text-red-500 animate-pulse" : "text-[#FF6B35]"}`}>{String(s).padStart(2, "0")}</p>
        <p className="text-[8px] text-muted-foreground">SEC</p>
      </div>
    </div>
  );
}

function AuctionCard({ auction, onBid, bidding, user, navigate }) {
  const Icon = TYPE_ICONS[auction.type] || Sparkles;
  const colors = TYPE_COLORS[auction.type] || TYPE_COLORS.custom;
  const isLastBidder = auction.last_bidder_id === user?.id;
  const minBid = auction.current_bid === 0 ? auction.starting_price : auction.current_bid + 1;
  const [bidAmount, setBidAmount] = useState("");
  const isExpired = new Date(auction.auction_end) <= new Date();
  const isWinner = auction.winner_id === user?.id;

  const quickBids = [
    minBid,
    Math.max(minBid, Math.ceil(minBid * 1.2)),
    Math.max(minBid, Math.ceil(minBid * 1.5)),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`auction-${auction.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${colors} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-sm text-card-foreground truncate">{auction.title}</h3>
            <button
              onClick={() => navigate(`/artist/${auction.artist_id}`)}
              className="text-[10px] text-muted-foreground hover:text-[#FF6B35] transition-colors"
            >
              par {auction.artist_name}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {auction.sniper_alert && !isExpired && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-500 animate-pulse" data-testid={`sniper-alert-${auction.id}`}>
              <AlertTriangle className="w-3 h-3" />
              Sniper !
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-card-foreground/80 border border-border">
            {auction.type_label}
          </span>
        </div>
      </div>

      {auction.description && (
        <p className="px-5 text-xs text-card-foreground/80 line-clamp-2">{auction.description}</p>
      )}

      {/* Lot image */}
      {auction.image_url && (
        <div className="px-5 pt-2">
          <img
            src={auction.image_url}
            alt={auction.title}
            className="w-full h-40 object-cover rounded-xl border border-border"
            data-testid={`auction-image-${auction.id}`}
          />
        </div>
      )}

      {/* Countdown + Current Bid */}
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] text-muted-foreground mb-1">
              {isExpired ? "Enchere terminee" : "Fin dans"}
            </p>
            <CountdownDisplay endDate={auction.auction_end} compact />
          </div>
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground mb-0.5">
              {auction.current_bid === 0 ? "Mise de depart" : "Derniere mise"}
            </p>
            <p className="font-mono-credits font-bold text-xl text-[#FF6B35]">
              {auction.current_bid === 0 ? auction.starting_price : auction.current_bid}
              <span className="text-xs ml-0.5">cr</span>
            </p>
          </div>
        </div>

        {/* Last bidder info */}
        {auction.last_bidder_pseudo && (
          <div className={`flex items-center gap-2 p-2 rounded-xl mb-3 ${
            isLastBidder ? "bg-emerald-50 border border-emerald-200" : "bg-muted border border-border"
          }`}>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center overflow-hidden">
              {auction.last_bidder_avatar ? (
                <img src={auction.last_bidder_avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-[8px] font-bold">{auction.last_bidder_pseudo[0].toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-card-foreground/80">
                {isLastBidder ? (
                  <span className="font-bold text-emerald-600">Tu es en tete !</span>
                ) : (
                  <><span className="font-bold">{auction.last_bidder_pseudo}</span> mene</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Users className="w-3 h-3" />
              {auction.unique_bidders || 0}
            </div>
          </div>
        )}

        {/* Winner display for ended auctions */}
        {isExpired && auction.winner_id && (
          <div className={`flex items-center gap-2 p-3 rounded-xl mb-3 ${
            isWinner ? "bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200" : "bg-muted border border-border"
          }`}>
            <Trophy className={`w-5 h-5 ${isWinner ? "text-yellow-500" : "text-muted-foreground"}`} />
            <p className="text-xs">
              {isWinner ? (
                <span className="font-bold text-yellow-600">Tu as remporte cette enchere !</span>
              ) : (
                <span className="text-card-foreground/80">Remporte par <strong>{auction.winner_pseudo}</strong></span>
              )}
            </p>
          </div>
        )}

        {/* My bid info */}
        {(auction.my_total_bid || 0) > 0 && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-2">
            <span>Tes mises : {auction.my_bid_count} encheres</span>
            <span className="font-mono-credits font-bold text-red-400">-{auction.my_total_bid} cr</span>
          </div>
        )}

        {/* Bid Actions (only for active auctions) */}
        {!isExpired && auction.status === "active" && auction.creator_id !== user?.id && (
          <div className="space-y-2">
            {/* Quick bid buttons */}
            <div className="flex gap-1.5">
              {quickBids.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setBidAmount(String(amt))}
                  disabled={(user?.credits_balance || 0) < amt}
                  className={`flex-1 text-xs py-2 rounded-full font-mono-credits border transition-all ${
                    bidAmount === String(amt)
                      ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35] font-bold"
                      : "bg-white border-border text-muted-foreground hover:border-[#FF6B35]/20"
                  } disabled:opacity-30`}
                >
                  {amt}
                </button>
              ))}
            </div>

            {/* Custom bid + submit */}
            <div className="flex gap-2">
              <input
                type="number"
                placeholder={`Min: ${minBid}`}
                min={minBid}
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                className="flex-1 bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none font-mono-credits"
                data-testid={`bid-input-${auction.id}`}
              />
              <button
                onClick={() => { onBid(auction.id, parseInt(bidAmount)); setBidAmount(""); }}
                disabled={bidding === auction.id || !bidAmount || parseInt(bidAmount) < minBid || (user?.credits_balance || 0) < parseInt(bidAmount)}
                className="bg-[#FF6B35] text-white font-bold px-5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 shrink-0 flex items-center gap-1"
                data-testid={`bid-btn-${auction.id}`}
              >
                <Gavel className="w-3.5 h-3.5" />
                {bidding === auction.id ? "..." : "Miser"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className="flex items-center justify-between px-5 py-2 bg-muted border-t border-border">
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <TrendingUp className="w-3 h-3" />
          {auction.total_bids} mise{auction.total_bids > 1 ? "s" : ""}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Users className="w-3 h-3" />
          {auction.unique_bidders || 0} encherisseur{(auction.unique_bidders || 0) > 1 ? "s" : ""}
        </div>
      </div>
    </motion.div>
  );
}

export default function RewardsPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("live");
  const [typeFilter, setTypeFilter] = useState("");
  const [bidding, setBidding] = useState(null);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const status = tab === "live" ? "active" : tab === "ended" ? "ended" : undefined;
      const [auctionsRes, claimsRes] = await Promise.all([
        api.get("/rewards", { params: { type: typeFilter || undefined, status, limit: 50 } }),
        api.get("/rewards/my-claims"),
      ]);
      setAuctions(auctionsRes.data.rewards);
      setMyClaims(claimsRes.data.claims);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, typeFilter, tab]);

  useEffect(() => {
    fetchData();
    // Poll for updates every 15 seconds
    pollRef.current = setInterval(fetchData, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  const handleBid = async (auctionId, amount) => {
    if (!amount || amount <= 0) return;
    setBidding(auctionId);
    try {
      const res = await api.post(`/rewards/${auctionId}/bid`, { amount });
      haptic(60);
      popCredits(-amount);
      if (res.data.sniper_alert) {
        toast.success("Sniper Alert ! Le temps a ete prolonge de 30 secondes.");
        fireConfetti("light");
      } else {
        toast.success(`Mise placee : ${amount} credits`);
      }
      refreshUser();
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (detail === "FOLLOW_REQUIRED") {
        const auction = activeAuctions.find(a => a.id === auctionId) || endedAuctions.find(a => a.id === auctionId);
        if (window.confirm(`Tu dois suivre ${auction?.artist_name || "cet artiste"} pour encherir. Veux-tu le suivre maintenant ?`)) {
          try {
            await api.post(`/artists/${auction?.artist_id}/follow`);
            toast.success("Tu suis maintenant cet artiste ! Tu peux relancer ton enchere.");
            fetchData();
          } catch { toast.error("Erreur lors du suivi"); }
        }
      } else if (detail.startsWith("BID_BANNED:")) {
        const days = detail.split(":")[1];
        toast.error(`Tu ne peux pas encherir pendant encore ${days} jour(s). Patience !`);
      } else {
        toast.error(detail);
      }
    }
    setBidding(null);
  };

  const types = [
    { key: "", label: "Tous" },
    { key: "shoutout", label: "Dedicaces" },
    { key: "exclusive", label: "Exclusifs" },
    { key: "merch", label: "Merch" },
    { key: "meet", label: "Rencontres" },
    { key: "ticket", label: "Concerts" },
  ];

  return (
    <div className="py-6 space-y-5" data-testid="rewards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
            <Gavel className="w-6 h-6 text-[#FF6B35]" />
            Encheres
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Mise sur des lots exclusifs d'artistes</p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-full px-3 py-1.5">
          <Coins className="w-4 h-4 text-[#FF6B35]" />
          <span className="font-mono-credits font-bold text-sm text-card-foreground" data-testid="rewards-balance">
            {user?.credits_balance || 0}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-foreground/5 rounded-2xl p-1 border border-foreground/5">
        {[
          { id: "live", label: "En cours", icon: Flame },
          { id: "ended", label: "Terminees", icon: Trophy },
          { id: "wins", label: "Mes gains", icon: Star },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
              tab === t.id
                ? "bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/20"
                : "text-muted-foreground hover:text-[#AFA8BA]"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            {t.id === "wins" && myClaims.length > 0 && (
              <span className="font-mono-credits text-[10px]">({myClaims.length})</span>
            )}
          </button>
        ))}
      </div>

      {/* Type filters (for live/ended) */}
      {tab !== "wins" && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {types.map((t) => (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap border transition-all shrink-0 ${
                typeFilter === t.key
                  ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                  : "bg-card border-border text-muted-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        {tab === "live" && (
          <motion.div key="live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            {loading ? (
              [...Array(3)].map((_, i) => <div key={i} className="bg-card rounded-3xl h-44 animate-pulse" />)
            ) : auctions.length === 0 ? (
              <div className="text-center py-12 px-4 bg-card border border-border rounded-3xl">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B35]/15 to-[#7000FF]/15 flex items-center justify-center mx-auto mb-4">
                  <Gavel className="w-8 h-8 text-[#FF6B35]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground mb-2">Aucune enchere en cours</h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-xs mx-auto">
                  Suis tes artistes preferes pour etre alerte des qu'ils lancent une enchere exclusive
                </p>
                <button
                  onClick={() => navigate("/discover")}
                  className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-5 py-2.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform"
                  data-testid="discover-from-empty-bids"
                >
                  Decouvrir des artistes
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              auctions.map((a) => (
                <AuctionCard key={a.id} auction={a} onBid={handleBid} bidding={bidding} user={user} navigate={navigate} />
              ))
            )}
          </motion.div>
        )}

        {tab === "ended" && (
          <motion.div key="ended" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            {loading ? (
              [...Array(3)].map((_, i) => <div key={i} className="bg-card rounded-3xl h-36 animate-pulse" />)
            ) : auctions.length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-foreground mb-2">Aucune enchere terminee</h3>
                <p className="text-sm text-muted-foreground">Les encheres terminees apparaitront ici</p>
              </div>
            ) : (
              auctions.map((a) => (
                <AuctionCard key={a.id} auction={a} onBid={handleBid} bidding={bidding} user={user} navigate={navigate} />
              ))
            )}
          </motion.div>
        )}

        {tab === "wins" && (
          <motion.div key="wins" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
            {myClaims.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-foreground mb-2">Aucun gain</h3>
                <p className="text-sm text-muted-foreground">Remporte une enchere pour voir tes gains ici</p>
              </div>
            ) : (
              myClaims.map((claim) => {
                const Icon = TYPE_ICONS[claim.reward_type] || Sparkles;
                return (
                  <div
                    key={claim.id}
                    className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl p-4 shadow-sm"
                    data-testid={`win-${claim.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center shrink-0">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-sm text-card-foreground truncate">{claim.reward_title}</p>
                        <p className="text-xs text-muted-foreground">par {claim.artist_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{claim.price} cr</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          claim.status === "completed" ? "bg-emerald-50 text-emerald-500" : "bg-yellow-100 text-yellow-600"
                        }`}>
                          {claim.status === "pending" ? "En attente" : "Remporte"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <ArtistCreateFab tab="rewards" createFlag="reward" label="Creer une enchere" testId="create-reward-fab" />
    </div>
  );
}
